/**
 * Admin Dashboard - Lead management interface
 * Mobile-first design with enhanced UX
 */

import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  Users,
  FileText,
  AlertCircle,
  Clock,
  Phone,
  Send,
  Trophy,
  XCircle,
  Eye,
  ChevronDown,
  Search,
  ArrowUpRight,
  Filter,
  Download,
  Calendar
} from 'lucide-react';
import styles from './AdminDashboard.module.css';
import { getApiUrl } from '../../config/environments';

// Tipo para los filtros de fecha
type DateFilter = 'all' | 'today' | 'week' | 'month';

// Configuración de filtros de fecha
// Cada filtro tiene un label para mostrar y una función que calcula
// la fecha mínima desde la cual mostrar leads
const DATE_FILTER_CONFIG: Record<DateFilter, { label: string; getDaysAgo: () => number | null }> = {
  all: {
    label: 'Todo',
    getDaysAgo: () => null, // null = sin filtro
  },
  today: {
    label: 'Hoy',
    getDaysAgo: () => 0, // 0 días atrás = hoy
  },
  week: {
    label: 'Esta semana',
    getDaysAgo: () => 7, // Últimos 7 días
  },
  month: {
    label: 'Este mes',
    getDaysAgo: () => 30, // Últimos 30 días
  },
};

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'Nuevo', cssClass: styles.statusSelectNew, icon: AlertCircle },
  reviewing: { label: 'En revisión', cssClass: styles.statusSelectReviewing, icon: Clock },
  contacted: { label: 'Contactado', cssClass: styles.statusSelectContacted, icon: Phone },
  proposal_sent: { label: 'Propuesta enviada', cssClass: styles.statusSelectProposalSent, icon: Send },
  won: { label: 'Ganado', cssClass: styles.statusSelectWon, icon: Trophy },
  lost: { label: 'Perdido', cssClass: styles.statusSelectLost, icon: XCircle },
};

interface LeadSummary {
  id: string;
  user_email?: string;
  company_name?: string;
  industry?: string;
  role?: string;
  status: string;
  lead_status: string;
  created_at: string;
  has_report: boolean;
  urgency_score?: number;
}

interface AdminDashboardData {
  assessments: LeadSummary[];
  total: number;
  filtered: number;
}

export function AdminDashboard() {
  const [data, setData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<'date' | 'urgency' | 'company'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const { getToken } = useAuth();

  // Fetch assessments
  useEffect(() => {
    fetchAssessments();
  }, [statusFilter]);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (statusFilter) {
        params.append('status_filter', statusFilter);
      }

      const token = await getToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(
        `${getApiUrl()}/reports/admin/assessments?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch assessments');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (assessmentId: string, newStatus: string) => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(
        `${getApiUrl()}/reports/admin/assessments/${assessmentId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      // Refresh the list
      fetchAssessments();
    } catch (err) {
      alert('Error updating status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const assessments = data?.assessments || [];
    const total = assessments.length;
    const newLeads = assessments.filter(a => a.lead_status === 'new').length;
    const won = assessments.filter(a => a.lead_status === 'won').length;
    const inProgress = assessments.filter(a => 
      ['reviewing', 'contacted', 'proposal_sent'].includes(a.lead_status)
    ).length;
    
    // Calculate conversion rate
    const conversionRate = total > 0 ? Math.round((won / total) * 100) : 0;
    
    // Calculate high urgency leads
    const highUrgency = assessments.filter(a => (a.urgency_score || 0) >= 7).length;
    
    return {
      total,
      newLeads,
      won,
      inProgress,
      conversionRate,
      highUrgency
    };
  }, [data]);

  // Filter and sort assessments
  const filteredAndSortedAssessments = useMemo(() => {
    let assessments = data?.assessments || [];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      assessments = assessments.filter(a =>
        (a.company_name || '').toLowerCase().includes(query) ||
        (a.user_email || '').toLowerCase().includes(query) ||
        (a.industry || '').toLowerCase().includes(query) ||
        (a.role || '').toLowerCase().includes(query)
      );
    }

    // Apply date filter
    // Esta lógica filtra los leads según el rango de fechas seleccionado
    const daysAgo = DATE_FILTER_CONFIG[dateFilter].getDaysAgo();
    if (daysAgo !== null) {
      // Calcular la fecha límite (hoy a las 00:00:00 menos los días)
      const now = new Date();
      const cutoffDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - daysAgo, // Resta los días
        0, 0, 0, 0 // Hora 00:00:00.000
      );

      // Filtrar: solo mantener leads creados después de cutoffDate
      assessments = assessments.filter(a => {
        const createdAt = new Date(a.created_at);
        return createdAt >= cutoffDate;
      });
    }

    // Apply sorting
    assessments = [...assessments].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'date':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'urgency':
          comparison = (a.urgency_score || 0) - (b.urgency_score || 0);
          break;
        case 'company':
          comparison = (a.company_name || '').localeCompare(b.company_name || '');
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return assessments;
  }, [data, searchQuery, sortField, sortDirection, dateFilter]);

  const toggleSort = (field: 'date' | 'urgency' | 'company') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  /**
   * Exporta los leads filtrados a un archivo CSV
   *
   * Cómo funciona:
   * 1. Define los encabezados del CSV (las columnas)
   * 2. Convierte cada lead a una fila de texto
   * 3. Escapa las comas y comillas para evitar errores
   * 4. Crea un "Blob" (archivo en memoria) con el contenido
   * 5. Crea un link temporal y simula un click para descargar
   */
  const exportToCSV = () => {
    // Paso 1: Definir las columnas del CSV
    const headers = [
      'Empresa',
      'Email',
      'Industria',
      'Rol',
      'Status',
      'Urgencia',
      'Fecha de Creación'
    ];

    // Paso 2: Convertir cada lead a una fila
    // La función escapeCSV maneja casos especiales como comas en el texto
    const escapeCSV = (value: string | undefined | null): string => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      // Si tiene comas, comillas o saltos de línea, envolver en comillas
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Convertir urgencia numérica a texto legible
    const getUrgencyText = (score?: number): string => {
      if (!score) return 'N/A';
      if (score >= 7) return 'Alta';
      if (score >= 4) return 'Media';
      return 'Baja';
    };

    // Obtener el label del status en español
    const getStatusLabel = (status: string): string => {
      return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label || status;
    };

    // Paso 3: Crear las filas de datos
    const rows = filteredAndSortedAssessments.map(assessment => [
      escapeCSV(assessment.company_name),
      escapeCSV(assessment.user_email),
      escapeCSV(assessment.industry),
      escapeCSV(assessment.role),
      escapeCSV(getStatusLabel(assessment.lead_status)),
      escapeCSV(getUrgencyText(assessment.urgency_score)),
      escapeCSV(new Date(assessment.created_at).toLocaleDateString('es-CR'))
    ].join(','));

    // Paso 4: Unir encabezados + filas con saltos de línea
    const csvContent = [headers.join(','), ...rows].join('\n');

    // Paso 5: Crear el archivo y descargarlo
    // BOM (Byte Order Mark) para que Excel reconozca los acentos correctamente
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Crear un link temporal para la descarga
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Nombre del archivo con fecha actual
    const today = new Date().toISOString().split('T')[0]; // Formato: 2024-01-15
    link.setAttribute('href', url);
    link.setAttribute('download', `leads_${today}.csv`);

    // Simular click y limpiar
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Liberar memoria
  };

  const getUrgencyBadge = (score?: number) => {
    if (!score) {
      return (
        <span className={styles.urgencyBadge + ' ' + styles.urgencyBadgeNA}>
          <span className={styles.urgencyDot + ' ' + styles.urgencyDotLow} />
          N/A
        </span>
      );
    }

    let badgeClass = styles.urgencyBadgeLow;
    let dotClass = styles.urgencyDotLow;
    let label = 'Baja';

    if (score >= 7) {
      badgeClass = styles.urgencyBadgeHigh;
      dotClass = styles.urgencyDotHigh;
      label = 'Alta';
    } else if (score >= 4) {
      badgeClass = styles.urgencyBadgeMedium;
      dotClass = styles.urgencyDotMedium;
      label = 'Media';
    }

    return (
      <span className={`${styles.urgencyBadge} ${badgeClass}`}>
        <span className={`${styles.urgencyDot} ${dotClass}`} />
        {label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <AlertCircle className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Error</h2>
          <p className={styles.errorMessage}>{error}</p>
          <button
            onClick={fetchAssessments}
            className={styles.retryButton}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div>
            <h1 className={styles.headerTitle}>
              <div className={styles.headerIcon}>
                <Users />
              </div>
              <span className={styles.headerTitleText}>Dashboard Admin</span>
            </h1>
            <p className={styles.headerSubtitle}>
              Gestiona tus leads y assessments
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Stats Cards */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <div className={styles.statCardIcon + ' ' + styles.statCardIconPrimary}>
                <Users size={20} />
              </div>
              <span className={styles.statCardTrend + ' ' + styles.statCardTrendUp}>
                <ArrowUpRight size={16} />
              </span>
            </div>
            <div className={styles.statCardContent}>
              <p className={styles.statCardLabel}>Total Leads</p>
              <p className={styles.statCardValue}>{stats.total}</p>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <div className={styles.statCardIcon + ' ' + styles.statCardIconWarning}>
                <AlertCircle size={20} />
              </div>
            </div>
            <div className={styles.statCardContent}>
              <p className={styles.statCardLabel}>Nuevos</p>
              <p className={styles.statCardValue}>{stats.newLeads}</p>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <div className={styles.statCardIcon + ' ' + styles.statCardIconInfo}>
                <Clock size={20} />
              </div>
            </div>
            <div className={styles.statCardContent}>
              <p className={styles.statCardLabel}>En Progreso</p>
              <p className={styles.statCardValue}>{stats.inProgress}</p>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHeader}>
              <div className={styles.statCardIcon + ' ' + styles.statCardIconSuccess}>
                <Trophy size={20} />
              </div>
              <span className={styles.statCardTrend + ' ' + styles.statCardTrendUp}>
                <ArrowUpRight size={16} />
                {stats.conversionRate}%
              </span>
            </div>
            <div className={styles.statCardContent}>
              <p className={styles.statCardLabel}>Ganados</p>
              <p className={styles.statCardValue}>{stats.won}</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className={styles.controlsSection}>
          {/* Search Bar */}
          <div className={styles.searchBar}>
            <Search className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Buscar por empresa, email, industria..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          {/* Sort Controls - Desktop */}
          <div className={styles.sortControls}>
            <button
              onClick={() => toggleSort('date')}
              className={`${styles.sortButton} ${sortField === 'date' ? styles.sortButtonActive : ''}`}
            >
              Fecha {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => toggleSort('urgency')}
              className={`${styles.sortButton} ${sortField === 'urgency' ? styles.sortButtonActive : ''}`}
            >
              Urgencia {sortField === 'urgency' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => toggleSort('company')}
              className={`${styles.sortButton} ${sortField === 'company' ? styles.sortButtonActive : ''}`}
            >
              Empresa {sortField === 'company' && (sortDirection === 'asc' ? '↑' : '↓')}
            </button>

            {/* Botón Exportar CSV */}
            <button
              onClick={exportToCSV}
              className={styles.exportButton}
              title="Exportar leads filtrados a CSV"
            >
              <Download size={16} />
              <span>Exportar</span>
            </button>
          </div>
        </div>
        {/* Status Filters */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersSectionHeader}>
            <Filter className={styles.filtersIcon} size={18} />
            <h2 className={styles.filtersTitle}>Filtrar por Status</h2>
          </div>
          <div className={styles.filtersContainer}>
            <button
              onClick={() => setStatusFilter(null)}
              className={`${styles.filterButton} ${statusFilter === null ? styles.filterButtonActive : ''}`}
            >
              <span className={styles.filterDot} />
              <span className={styles.filterButtonLabel}>Todos</span>
              <span className={styles.filterButtonCount}>({stats.total})</span>
            </button>
            {Object.entries(STATUS_CONFIG).map(([status, config]) => {
              const count = (data?.assessments || []).filter((a) => a.lead_status === status).length;
              const Icon = config.icon;
              return (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`${styles.filterButton} ${statusFilter === status ? styles.filterButtonActive : ''}`}
                >
                  <Icon className={styles.filterIcon} />
                  <span className={styles.filterButtonLabel}>{config.label}</span>
                  <span className={styles.filterButtonCount}>({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Date Filters */}
        <div className={styles.filtersSection}>
          <div className={styles.filtersSectionHeader}>
            <Calendar className={styles.filtersIcon} size={18} />
            <h2 className={styles.filtersTitle}>Filtrar por Fecha</h2>
          </div>
          <div className={styles.filtersContainer}>
            {/* Iteramos sobre las opciones de filtro de fecha */}
            {(Object.entries(DATE_FILTER_CONFIG) as [DateFilter, typeof DATE_FILTER_CONFIG[DateFilter]][]).map(
              ([key, config]) => (
                <button
                  key={key}
                  onClick={() => setDateFilter(key)}
                  className={`${styles.filterButton} ${dateFilter === key ? styles.filterButtonActive : ''}`}
                >
                  <span className={styles.filterDot} />
                  <span className={styles.filterButtonLabel}>{config.label}</span>
                </button>
              )
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className={styles.resultsInfo}>
          <p className={styles.resultsText}>
            Mostrando <strong>{filteredAndSortedAssessments.length}</strong> de <strong>{stats.total}</strong> leads
          </p>
        </div>

        {/* Assessments - Mobile Cards / Desktop Table */}
        <div className={styles.assessmentsContainer}>
          {/* Mobile: Cards View */}
          <div className={styles.mobileCardsView}>
            {filteredAndSortedAssessments.length === 0 ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>
                  <FileText />
                </div>
                <p className={styles.emptyStateTitle}>
                  {searchQuery ? 'No se encontraron resultados' : 'No hay assessments disponibles'}
                </p>
                <p className={styles.emptyStateDescription}>
                  {searchQuery 
                    ? 'Intenta con otros términos de búsqueda'
                    : 'Los leads aparecerán aquí cuando los clientes completen sus assessments'
                  }
                </p>
              </div>
            ) : (
              filteredAndSortedAssessments.map((assessment) => {
                const statusConfig = STATUS_CONFIG[assessment.lead_status as keyof typeof STATUS_CONFIG];
                const initial = (assessment.company_name || assessment.user_email || '?')[0].toUpperCase();
                
                return (
                  <div key={assessment.id} className={styles.leadCard}>
                    <div className={styles.leadCardHeader}>
                      <div className={styles.leadCell}>
                        <div className={styles.leadAvatar}>
                          <span className={styles.leadAvatarText}>{initial}</span>
                        </div>
                        <div className={styles.leadInfo}>
                          <div className={styles.leadName}>
                            {assessment.company_name || 'Sin nombre'}
                          </div>
                          <div className={styles.leadEmail}>
                            {assessment.user_email || 'Sin email'}
                          </div>
                        </div>
                      </div>
                      {getUrgencyBadge(assessment.urgency_score)}
                    </div>
                    
                    <div className={styles.leadCardBody}>
                      <div className={styles.leadCardRow}>
                        <span className={styles.leadCardLabel}>Industria:</span>
                        <span className={styles.leadCardValue}>{assessment.industry || 'N/A'}</span>
                      </div>
                      <div className={styles.leadCardRow}>
                        <span className={styles.leadCardLabel}>Rol:</span>
                        <span className={styles.leadCardValue}>{assessment.role || 'N/A'}</span>
                      </div>
                      <div className={styles.leadCardRow}>
                        <span className={styles.leadCardLabel}>Fecha:</span>
                        <span className={styles.leadCardValue}>
                          {new Date(assessment.created_at).toLocaleDateString('es-CR', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className={styles.leadCardRow}>
                        <span className={styles.leadCardLabel}>Status:</span>
                        <div className={styles.statusSelectWrapper}>
                          <select
                            value={assessment.lead_status}
                            onChange={(e) => handleStatusChange(assessment.id, e.target.value)}
                            className={`${styles.statusSelect} ${statusConfig?.cssClass || styles.statusSelectDefault}`}
                          >
                            {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                              <option key={status} value={status}>
                                {config.label}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className={styles.statusSelectIcon} />
                        </div>
                      </div>
                    </div>
                    
                    <div className={styles.leadCardFooter}>
                      <Link
                        to={`/admin/reports/${assessment.id}`}
                        className={styles.viewButton}
                      >
                        <Eye className={styles.viewButtonIcon} />
                        Ver Reporte
                      </Link>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Desktop: Table View */}
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead className={styles.tableHead}>
                <tr>
                  <th className={styles.tableHeader}>Lead</th>
                  <th className={styles.tableHeader}>Industria / Rol</th>
                  <th className={styles.tableHeader}>Status</th>
                  <th className={styles.tableHeader}>Urgencia</th>
                  <th className={styles.tableHeader}>Fecha</th>
                  <th className={styles.tableHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {filteredAndSortedAssessments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.tableCell}>
                      <div className={styles.emptyState}>
                        <div className={styles.emptyStateIcon}>
                          <FileText />
                        </div>
                        <p className={styles.emptyStateTitle}>
                          {searchQuery ? 'No se encontraron resultados' : 'No hay assessments disponibles'}
                        </p>
                        <p className={styles.emptyStateDescription}>
                          {searchQuery 
                            ? 'Intenta con otros términos de búsqueda'
                            : 'Los leads aparecerán aquí cuando los clientes completen sus assessments'
                          }
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedAssessments.map((assessment) => {
                    const statusConfig = STATUS_CONFIG[assessment.lead_status as keyof typeof STATUS_CONFIG];
                    const initial = (assessment.company_name || assessment.user_email || '?')[0].toUpperCase();
                    
                    return (
                      <tr key={assessment.id} className={styles.tableRow}>
                        <td className={styles.tableCell}>
                          <div className={styles.leadCell}>
                            <div className={styles.leadAvatar}>
                              <span className={styles.leadAvatarText}>{initial}</span>
                            </div>
                            <div className={styles.leadInfo}>
                              <div className={styles.leadName}>
                                {assessment.company_name || 'Sin nombre'}
                              </div>
                              <div className={styles.leadEmail}>
                                {assessment.user_email || 'Sin email'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={styles.tableCell}>
                          <div className={styles.industryText}>{assessment.industry || 'N/A'}</div>
                          <div className={styles.roleText}>{assessment.role || 'N/A'}</div>
                        </td>
                        <td className={styles.tableCell}>
                          <div className={styles.statusSelectWrapper}>
                            <select
                              value={assessment.lead_status}
                              onChange={(e) => handleStatusChange(assessment.id, e.target.value)}
                              className={`${styles.statusSelect} ${statusConfig?.cssClass || styles.statusSelectDefault}`}
                            >
                              {Object.entries(STATUS_CONFIG).map(([status, config]) => (
                                <option key={status} value={status}>
                                  {config.label}
                                </option>
                              ))}
                            </select>
                            <ChevronDown className={styles.statusSelectIcon} />
                          </div>
                        </td>
                        <td className={styles.tableCell}>
                          {getUrgencyBadge(assessment.urgency_score)}
                        </td>
                        <td className={styles.tableCell}>
                          <span className={styles.dateText}>
                            {new Date(assessment.created_at).toLocaleDateString('es-CR', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </td>
                        <td className={styles.tableCell}>
                          <Link
                            to={`/admin/reports/${assessment.id}`}
                            className={styles.viewButton}
                          >
                            <Eye className={styles.viewButtonIcon} />
                            Ver Reporte
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
